import {
  AccordionDetails,
  alpha,
  Box,
  Divider,
  InputAdornment,
  OutlinedInput,
  Stack,
  Switch,
  Theme,
  Typography,
} from '@mui/material';
import { Search, Settings } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import {
  FlatAccordion,
  FlatAccordionSummary,
} from 'src/components/FlatAccordion/flat-accordion';
import {
  type ModalAction,
  ModalContainer,
  useModalSteps,
} from 'src/components/ModalContainer';
import { filterAdornmentSx } from 'src/components/ModalContainer/modal-list.styles';
import {
  SidePanelLayoutEntry,
  SidePanelPersonalizationSection,
  SidePanelPropertyKey,
  SidePanelSectionKey,
  usePersonalizedSidePanel,
} from 'src/graphql/entities/side-panel-personalization';
import { ProfileMenu } from './profile-menu';
import { SaveProfileStep } from './save-profile-dialog';
import { SortablePropertyItem, SortablePropertyList } from './sortable-property-list';

interface PersonalizeSidePanelModalProps {
  open: boolean;
  onClose: () => void;
}

const SECTION_APPEARANCE: Record<
  SidePanelSectionKey,
  { icon: string; getColor: (theme: Theme) => string }
> = {
  conversation: { icon: 'messages-square', getColor: (theme) => theme.palette.info.main },
  site: { icon: 'building-2', getColor: (theme) => theme.palette.secondary.main },
  client: { icon: 'user', getColor: (theme) => theme.palette.warning.main },
};

const SectionBlock = ({
  section,
  properties,
  expanded,
  onToggle,
  disabled,
  reorderDisabled,
  onToggleVisible,
  onReorder,
}: {
  section: SidePanelPersonalizationSection;
  properties: SidePanelPersonalizationSection['properties'];
  expanded: boolean;
  onToggle: () => void;
  disabled: boolean;
  reorderDisabled: boolean;
  onToggleVisible: (key: SidePanelPropertyKey) => void;
  onReorder: (nextKeys: SidePanelPropertyKey[]) => void;
}) => {
  const items = useMemo<SortablePropertyItem[]>(
    () =>
      properties.map(({ descriptor, visible }) => ({
        key: descriptor.key,
        label: descriptor.label,
        icon: descriptor.icon,
        iconLibrary: descriptor.iconLibrary,
        group: descriptor.group,
        visible,
        reorderable: descriptor.reorderable,
      })),
    [properties],
  );

  const shownCount = section.properties.filter((property) => property.visible).length;
  const { icon, getColor } = SECTION_APPEARANCE[section.key];

  return (
    <FlatAccordion
      expanded={expanded}
      onChange={onToggle}
      disableGutters
      data-testid={`personalize-side-panel-section-${section.key}`}
    >
      <FlatAccordionSummary
        expandIcon={<DynamicIcon name="chevron-down" library="lucide" />}
        data-testid={`personalize-side-panel-section-toggle-${section.key}`}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          sx={{ flexGrow: 1, minWidth: 0, py: 1 }}
        >
          <Box
            sx={(theme) => ({
              width: '2rem',
              height: '2rem',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: getColor(theme),
              backgroundColor: alpha(getColor(theme), 0.12),
            })}
          >
            <DynamicIcon name={icon} library="lucide" size={20} />
          </Box>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }} noWrap>
            {section.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, pr: 0.5 }}>
            {shownCount} of {section.properties.length} shown
          </Typography>
        </Stack>
      </FlatAccordionSummary>
      <AccordionDetails sx={{ p: 0, pb: 1 }}>
        <SortablePropertyList
          items={items}
          disabled={disabled}
          reorderDisabled={reorderDisabled}
          onToggleVisible={onToggleVisible}
          onReorder={onReorder}
        />
      </AccordionDetails>
    </FlatAccordion>
  );
};

interface PersonalizeSidePanelBodyProps {
  editsDisabled: boolean;
  profilesAnchorEl: HTMLElement | null;
  onProfilesClose: () => void;
}

const PersonalizeSidePanelBody = ({
  editsDisabled,
  profilesAnchorEl,
  onProfilesClose,
}: PersonalizeSidePanelBodyProps) => {
  const {
    viewModel: {
      sections,
      effectiveLayout,
      hideEmpty,
      isDraft,
      activeProfile,
      profiles,
      actions,
    },
  } = usePersonalizedSidePanel();
  const { pushStep } = useModalSteps();

  // the first section starts expanded, the rest collapsed
  const [expandedSections, setExpandedSections] = useState<
    Partial<Record<SidePanelSectionKey, boolean>>
  >({});

  const [searchTerm, setSearchTerm] = useState('');

  const search = searchTerm.trim().toLowerCase();
  const searching = search !== '';

  // card-rendered surfaces have no draggable rows in any section, so the
  // "drag to reorder" hint would be misleading
  const canReorder = useMemo(
    () => sections.some((section) => !section.reorderDisabled),
    [sections],
  );

  const sectionsToRender = useMemo(
    () =>
      sections
        .map((section) => ({
          section,
          properties: searching
            ? section.properties.filter(({ descriptor }) =>
                descriptor.label.toLowerCase().includes(search),
              )
            : section.properties,
        }))
        .filter(({ properties }) => !searching || properties.length > 0),
    [sections, searching, search],
  );

  const updateSection = useCallback(
    (
      sectionKey: SidePanelSectionKey,
      mapProperties: (properties: SidePanelLayoutEntry[]) => SidePanelLayoutEntry[],
    ) =>
      actions.updateDraft({
        ...effectiveLayout,
        sections: {
          ...effectiveLayout.sections,
          [sectionKey]: {
            properties: mapProperties(effectiveLayout.sections[sectionKey]?.properties ?? []),
          },
        },
      }),
    [actions, effectiveLayout],
  );

  const toggleVisible = useCallback(
    (sectionKey: SidePanelSectionKey, propertyKey: SidePanelPropertyKey) =>
      updateSection(sectionKey, (properties) =>
        properties.map((property) =>
          property.key === propertyKey
            ? { ...property, visible: !property.visible }
            : property,
        ),
      ),
    [updateSection],
  );

  const reorderSection = useCallback(
    (sectionKey: SidePanelSectionKey, nextKeys: SidePanelPropertyKey[]) =>
      updateSection(sectionKey, (properties) => {
        const byKey = new Map(properties.map((property) => [property.key, property]));
        return nextKeys.flatMap((key) => byKey.get(key) ?? []);
      }),
    [updateSection],
  );

  const toggleHideEmpty = useCallback(
    () => actions.updateDraft({ ...effectiveLayout, hideEmpty: !hideEmpty }),
    [actions, effectiveLayout, hideEmpty],
  );

  const ownProfileNames = useMemo(
    () => profiles.filter((profile) => profile.source === 'user').map((p) => p.name),
    [profiles],
  );

  const saveAsProfile = async () => {
    const name = await pushStep<string>(<SaveProfileStep existingNames={ownProfileNames} />);
    if (name) {
      actions.saveAsProfile(name);
    }
  };

  return (
    <Stack gap={1}>
      <ProfileMenu
        anchorEl={profilesAnchorEl}
        onClose={onProfilesClose}
        profiles={profiles}
        activeProfileId={activeProfile?.id ?? null}
        isDraft={isDraft}
        onSelect={actions.applyProfile}
        onDelete={actions.deleteProfile}
        onSaveAs={() => void saveAsProfile()}
      />

      <OutlinedInput
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        // The container submits its primary on Enter; searching should not close the dialog.
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
          }
        }}
        fullWidth
        placeholder="Search properties"
        startAdornment={
          <InputAdornment position="start" sx={filterAdornmentSx}>
            <Search aria-hidden />
          </InputAdornment>
        }
      />

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2">Hide empty fields</Typography>
        <Switch
          checked={hideEmpty}
          disabled={editsDisabled}
          onChange={toggleHideEmpty}
          inputProps={{ 'aria-label': 'Hide empty fields' }}
        />
      </Stack>

      {canReorder ? (
        <Typography variant="caption" color="text.secondary">
          Drag to reorder · click the eye to show or hide.
        </Typography>
      ) : null}

      <Divider />
      <Stack divider={<Divider />}>
        {sectionsToRender.map(({ section, properties }) => {
          // the first section is expanded by default; keyed on the section
          // itself so a filtered search can't shift which one that is
          const defaultExpanded = section.key === sections[0]?.key;

          return (
            <SectionBlock
              key={section.key}
              section={section}
              properties={properties}
              expanded={searching || (expandedSections[section.key] ?? defaultExpanded)}
              onToggle={() =>
                setExpandedSections((current) => ({
                  ...current,
                  [section.key]: !(current[section.key] ?? defaultExpanded),
                }))
              }
              disabled={editsDisabled}
              reorderDisabled={searching || Boolean(section.reorderDisabled)}
              onToggleVisible={(propertyKey) => toggleVisible(section.key, propertyKey)}
              onReorder={(nextKeys) => reorderSection(section.key, nextKeys)}
            />
          );
        })}
      </Stack>
      {searching && sectionsToRender.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          No matching properties.
        </Typography>
      ) : null}
    </Stack>
  );
};

export const PersonalizeSidePanelModal = ({
  open,
  onClose,
}: PersonalizeSidePanelModalProps) => {
  const {
    viewModel: { sections, isDraft, loading, actions },
  } = usePersonalizedSidePanel();

  const [profilesAnchorEl, setProfilesAnchorEl] = useState<HTMLElement | null>(null);

  // while the view model loads, the effective layout is a builtin-default
  // fallback — an edit would persist it and clobber the stored profile selection
  const editsDisabled = loading || sections.some((section) => section.loading);

  const done: ModalAction = {
    label: 'Done',
    kind: 'primary',
    onClick: onClose,
    testId: 'personalize-side-panel-done',
  };

  return (
    <ModalContainer
      open={open}
      onClose={onClose}
      size="medium"
      title="Personalize this view"
      data-testid="personalize-side-panel-modal"
      headerActions={[
        {
          label: 'View profiles',
          icon: <Settings />,
          onClick: (event) => setProfilesAnchorEl(event.currentTarget),
          disabled: editsDisabled,
          testId: 'personalize-side-panel-profiles',
        },
      ]}
      actions={
        isDraft
          ? [
              {
                label: 'Reset',
                kind: 'secondary',
                onClick: actions.resetToProfile,
                testId: 'personalize-side-panel-reset',
              },
              done,
            ]
          : [done]
      }
    >
      <PersonalizeSidePanelBody
        editsDisabled={editsDisabled}
        profilesAnchorEl={profilesAnchorEl}
        onProfilesClose={() => setProfilesAnchorEl(null)}
      />
    </ModalContainer>
  );
};
